import { useState } from "react";
import { IconMessages, IconSend } from "@tabler/icons-react";
import { Button, Card, Group, Heading, Input, Stack, Text, ThemeIcon } from "@/components/ui";
import type { MensajeDeChat } from "@/lib/types";
import { formatFechaHora } from "@/shared/format/fecha";
import { useChatMensajes, useEnviarMensajeChat } from "../hooks/use-chat-mensajes";

/**
 * Card del chat bidireccional Solicitante ↔ Autorizador · solo texto
 * plano, persistente (decisión 36). Polling 3s. Compartido por el
 * detalle del solicitante y la pantalla de autorización.
 */
export function ChatCard({
  solicitudId,
  fill = false,
}: {
  solicitudId: string;
  /** Si true, la tarjeta llena la altura de su columna (área de mensajes flexible). */
  fill?: boolean;
}) {
  const [texto, setTexto] = useState("");
  const { data, isPending, isError, error } = useChatMensajes(solicitudId, true);
  const enviar = useEnviarMensajeChat(solicitudId, {
    onEnviado: () => setTexto(""),
  });

  return (
    <Card padding="lg" className={fill ? "flex h-full flex-col" : undefined}>
      <Stack gap="md" className={fill ? "min-h-0 flex-1" : undefined}>
        <Group gap="sm" align="flex-start" wrap="nowrap">
          <ThemeIcon size="md" radius="md" variant="filled" color="multiva">
            <IconMessages size={20} stroke={1.6} />
          </ThemeIcon>
          <div>
            <Heading order={5}>Chat de la solicitud</Heading>
            <Text size="sm" c="dimmed" className="mt-0.5">
              Bidireccional solicitante ↔ autorizador · solo texto, persistente.
            </Text>
          </div>
        </Group>

        <div
          className={
            "overflow-y-auto rounded-lg border border-border p-3 " +
            (fill ? "min-h-0 flex-1" : "max-h-80")
          }
          style={{ background: "light-dark(var(--color-slate-50), var(--color-ink-800))" }}
        >
          {isPending ? (
            <Text size="xs" c="dimmed">Cargando mensajes…</Text>
          ) : isError ? (
            <Text size="xs" c="peligro-700">{(error as Error).message}</Text>
          ) : data.length === 0 ? (
            <Text size="xs" c="dimmed">Sin mensajes aún.</Text>
          ) : (
            <Stack gap="xs">
              {data.map((m) => (
                <MensajeBubble key={m.id} mensaje={m} />
              ))}
            </Stack>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (texto.trim().length === 0) return;
            enviar.mutate(texto);
          }}
        >
          <Group gap="xs" wrap="nowrap">
            <Input
              wrapperClassName="flex-1"
              value={texto}
              onChange={(e) => setTexto(e.currentTarget.value)}
              placeholder="Escribe un mensaje…"
              maxLength={2000}
            />
            <Button
              type="submit"
              leftSection={<IconSend size={14} />}
              loading={enviar.isPending}
              disabled={texto.trim().length === 0}
            >
              Enviar
            </Button>
          </Group>
        </form>
      </Stack>
    </Card>
  );
}

/**
 * Un mensaje individual · colorea según rol del autor para que
 * sea visualmente obvio quién dijo qué.
 */
function MensajeBubble({ mensaje }: { mensaje: MensajeDeChat }) {
  const color =
    mensaje.rolAutor === "Autorizador"
      ? "info"
      : mensaje.rolAutor === "MesaDeControl"
        ? "azulProfundo"
        : "slate"; // Solicitante · azul-gris de marca (combina con el esquema)
  const etiqueta =
    mensaje.rolAutor === "MesaDeControl" ? "Mesa de Control" : mensaje.rolAutor;

  return (
    <div
      className="rounded-lg p-2.5"
      style={{
        background: `color-mix(in srgb, var(--color-${color}-600) 12%, transparent)`,
        border: `1px solid color-mix(in srgb, var(--color-${color}-600) 28%, transparent)`,
      }}
    >
      <Group justify="space-between" className="mb-1 opacity-75">
        <Text size="xs" tt="uppercase" fw={600} className="tracking-wide" style={{ fontSize: 10 }}>
          {etiqueta}
        </Text>
        <Text size="xs" style={{ fontSize: 10 }}>
          {formatFechaHora(mensaje.enviadoAt)}
          {mensaje.nivelDelFlujo ? ` · nivel ${mensaje.nivelDelFlujo}` : null}
        </Text>
      </Group>
      <Text size="sm" style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
        {mensaje.texto}
      </Text>
    </div>
  );
}
